import {
  ActivateHeadAgreementFetcherQuery,
  ActivateHeadAgreementFetcherQueryVariables,
} from "@/gql/graphql";
import { gql, useQuery } from "@apollo/client";
import { Spacing } from "@stenajs-webui/core";
import { ErrorScreen, LoadingScreen } from "@stenajs-webui/panels";
import * as React from "react";
import { canChangeHeadAgreementStatusToActive } from "../../details/features/existing-agreement/util/AgreementStatusUtil";
import { ActivateHeadAgreementContainer } from "./ActivateHeadAgreementContainer";

interface Props {
  headAgreementId: string;
  customerId: string;
}

const query = gql`
  fragment ActivateHeadAgreementRouteAgreements on RouteAgreement {
    id
    name

    statusCode
    routeDescription
    agreementNumber
    hasMatrix
    sailingTypeCodes
    rowVersion
    routes {
      id
      code
      routePair {
        code
      }
    }
    externalDescription
    multiLegAgrRouteId
    currency {
      id
      code
    }
    valid {
      start {
        isoString
      }
      end {
        isoString
      }
    }
  }

  fragment ActivateHeadAgrHeadAgr on HeadAgreement {
    id
    name
    breadCrumbName
    headAgreementNumber
    internalComments
    specifications
    condition
    lastPage
    statusCode
    rowVersion
    customer {
      id
      custIndex
      custNo
      name
      breadCrumbName
    }
    valid {
      start {
        isoString
      }
      end {
        isoString
      }
    }
    breadCrumbName

    routeAgreements {
      ...ActivateHeadAgreementRouteAgreements
    }
    multiLegAgrRoutes {
      id
      name
      currency {
        id
        code
        name
        shortDescription
        longDescription
      }
      statusCode
      valid {
        start {
          isoString
        }
        end {
          isoString
        }
      }
      dateCreated
      createdBy
      dateModified
      modifiedBy
      idNumber
      rowVersion
      workFlowStatusCode
      route {
        id
        code
        name
      }
    }
  }

  fragment ActivateCustomer on Customer {
    id
    name
    breadCrumbName
    custIndex
    custNo
    isParent
    custStatus {
      id
      name
    }
    parent {
      id
      preselectedHeadAgreement {
        id
      }
    }
    childCustomers {
      id
    }
    statisticalGrouping {
      id
    }
    invoiceCurrency {
      id
      code
    }
    seller {
      id
      name
    }
    salesRegion {
      id
      code
      name
    }
    isStandardCustomer
    hasRebateAgreements
    writeHistory {
      createdAt {
        isoString
      }
      createdBy {
        id
        name
      }
      modifiedAt {
        isoString
      }
      modifiedBy {
        id
        name
      }
    }
  }

  query ActivateHeadAgreementFetcher($headAgreementId: ID!, $customerId: ID!) {
    productPrice {
      customers {
        byId(id: $customerId) {
          ...ActivateCustomer
        }
      }
      headAgreement {
        byId(headAgreementId: $headAgreementId) {
          ...ActivateHeadAgrHeadAgr
        }
      }
    }
  }
`;

export const ActivateHeadAgreementFetcher: React.FC<Props> = ({
  headAgreementId,
  customerId,
}) => {
  const { data, loading, error } = useQuery<
    ActivateHeadAgreementFetcherQuery,
    ActivateHeadAgreementFetcherQueryVariables
  >(query, { variables: { headAgreementId, customerId } });

  const headAgreement = data?.productPrice.headAgreement.byId;
  const customer = data?.productPrice.customers.byId;

  if (loading) {
    return (
      <Spacing num={12}>
        <LoadingScreen />
      </Spacing>
    );
  }

  if (error) {
    return (
      <Spacing num={12}>
        <ErrorScreen text={error.message} />
      </Spacing>
    );
  }

  if (!headAgreement) {
    return (
      <Spacing num={12}>
        <ErrorScreen text={"Missing header agreement"} />
      </Spacing>
    );
  }

  if (!customer) {
    return (
      <Spacing num={12}>
        <ErrorScreen text={"Missing customer"} />
      </Spacing>
    );
  }

  if (!canChangeHeadAgreementStatusToActive(headAgreement.statusCode)) {
    return <ErrorScreen text={"Header agreement cannot be activated."} />;
  }

  return (
    <ActivateHeadAgreementContainer
      headAgreement={headAgreement}
      customer={customer}
    />
  );
};
